<template>
  <SfModalPortal
    modal-id="budget-delete-row-modal"
    size="s"
    :eager="true"
    @close="close"
  >
    <template #title>
      {{ deleteRowTitle }}
    </template>

    <template #body>
      <SfFlash :message="errorMessage" type="error" />
      <p>
        {{ areYouSureText }}
        <strong>{{ deleteRowTypeText }}</strong>
      </p>
    </template>

    <template #footer>
      <SfButtonDs variant="secondary" class="sf-level-item" @click="close">
        {{ cancelText }}
      </SfButtonDs>
      <SfButtonDs
        variant="danger"
        class="sf-level-item"
        :disabled="processing"
        data-testid="confirm-delete-btn"
        @click="deleteBudget"
      >
        {{ deleteText }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type { Action, AvailableActionsMap, BudgetEntry } from '../types'
import type { Ref } from 'vue'

import { ref } from 'vue'

import {
  getUrlOptionsFromBudgetTable,
  renderBudgetEntries,
} from '../handlers/budget-table-api'
import SfFlash from '@app/shared/vue/components/sf-flash.vue'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'
import Sfetch from '@app/utils/sfetch'

type SetActionFunction = (
  _value: string | null,
  _type: null | keyof AvailableActionsMap,
  _childActions: Record<string, Action>
) => void

interface Props {
  rowType: string
  rowId?: string
  elementName?: string
  setAction: SetActionFunction
}

const props = withDefaults(defineProps<Props>(), {
  elementName: '',
  rowId: '',
})

const emit = defineEmits(['close'])

type DeleteBudgetUrlOptions = {
  method: string
  body: { type: string | null; rowId: string | null }
}

const processing: Ref<boolean> = ref(false)
const errorMessage: Ref<string> = ref('')

const areYouSureText = window.I18n.t(`js.budget.delete_row.are_you_sure`)
const cancelText = window.I18n.t('confirmation.cancel')
const deleteText = window.I18n.t('confirmation.delete')
const deleteRowTitle = window.I18n.t('js.budget.delete_row.title')
const deleteRowTypeText = window.I18n.t(
  `js.budget.delete_row.${props.rowType}`,
  { element: props.elementName }
)

function deleteBudget(): void {
  const urlOptions = getUrlOptionsFromBudgetTable()
  const url = window.Silverfin.Routes.delete_budget_set_budget_entries_path(
    urlOptions.firm_id,
    urlOptions.company_id,
    urlOptions.budget_set_id,
    urlOptions.budget_set_id,
    { ...urlOptions }
  )

  const options = {
    method: 'DELETE',
    body: {
      type: props.rowType,
      rowId: props.rowId,
    },
  }

  makeRequest(url, options)
}

function close(): void {
  processing.value = false
  emit('close')
}

async function makeRequest(
  url: string,
  options: DeleteBudgetUrlOptions
): Promise<void> {
  processing.value = true

  try {
    const budgetEntries = await Sfetch.json<BudgetEntry[]>(url, options)

    renderBudgetEntries(budgetEntries ?? [])

    if (props.setAction) {
      props.setAction(null, null, {})
    }

    close()
  } catch {
    processing.value = false
    errorMessage.value = window.I18n.t(`js.budget.delete_row.error`)
  }
}
</script>
